import _ from 'lodash';
import { t } from '@lingui/macro';
import calculateSize from 'calculate-size';

import { metricDefFormatter, formatMetricDefValue } from '../../../../utilities';
import { COLUMN_TYPES } from '../../../data-table/DataTableColumns';
import format, { DEFAULT_INVALID_VALUE } from '../../../../utilities/FormattingUtilities';
import { getMetricStatusRendererWithThresholdMetrics } from '../MetricStatus';
import resultColumnRenderer from '../Result';
import { getThresholdsKeys, getThresholdsTooltip } from '../Thresholds';
import { getWiFiColumns, getWiFiGroupTestColumn } from './WiFiColumns';
import { buildThresholdColumns, getCustomMetrics } from '../TestLogUtilities';
import { semColumnRenderer, semColumnTooltip } from '../Sem';

/**
 * The metric names are stored as min, max, avg, count, we need to prefix the metric to get the right value.
 */
function modifyMetricName(metric) {
    if (metric.isString === true) {
        return metric.name;
    }

    return `avg${metric.name}`;
}

function buildCustomColumn(metric, test) {
    const mappingValue = modifyMetricName(metric);
    const baseConfig = {
        title: metric.title || DEFAULT_INVALID_VALUE,
        isMain: metric.isMain,
        mapping: mappingValue,
    };

    const thresholdMetrics = getThresholdsKeys(metric.name, test);

    // Gives string metrics more width, since string values are typically longer than numeric values.
    if (metric.isString === true) {
        return _.extend(baseConfig, {
            flex: 1,
            minWidth: 275,
        });
    }

    const renderer = thresholdMetrics?.critical
        ? {
              renderer: getMetricStatusRendererWithThresholdMetrics(test, thresholdMetrics),
              getTooltip: (row) => {
                  const degraded = formatMetricDefValue(metric, row[thresholdMetrics.degraded]);
                  const excessive = formatMetricDefValue(metric, row[thresholdMetrics.critical]);
                  return getThresholdsTooltip(degraded, excessive);
              },
          }
        : {
              tooltip: false,
          };

    const estimateSize = calculateSize(baseConfig.title, {
        font: 'Roboto',
        fontWeight: 400,
        fontSize: '18px',
    }).width;

    const width = Math.max(100, estimateSize + 34);

    const formatter = metricDefFormatter({ ...metric, forceDecimals: true });
    function stepFailedFormatterWrapper(value, row, csvConfig) {
        // CSVBuilder.getColumnUnits calls the formatter with `{ onlyUnits: true }` so it can
        // append "(units)" to the CSV header. Return just the units string in that case.
        if (row?.onlyUnits || csvConfig?.onlyUnits) {
            return _.isObject(formatter) ? formatter.baseUnits || '' : '';
        }

        const failedStep = row?.failed_step;
        const metricName = metric?.name;

        if (!_.isEmpty(failedStep) && failedStep === metricName) {
            return 'Failed';
        }

        if (_.isFunction(formatter)) {
            return formatter(value, row, csvConfig);
        }

        const formatterConfig = _.isString(formatter) ? { type: formatter } : formatter;
        // Honor CSV's `displayUnits: false` so units don't appear in every cell when
        // they're already present in the header.
        const finalConfig =
            csvConfig?.displayUnits === false ? { ...formatterConfig, displayUnits: false } : formatterConfig;
        return format(value, finalConfig);
    }

    const thresholdColumns = thresholdMetrics?.critical
        ? buildThresholdColumns(
              thresholdMetrics.degraded,
              thresholdMetrics.critical,
              metric.title,
              stepFailedFormatterWrapper
          )
        : {};

    return _.extend(baseConfig, {
        type: COLUMN_TYPES.NUMERIC,
        width,
        sortable: true,
        formatter: stepFailedFormatterWrapper,
        thresholdColumns,
        ...renderer,
    });
}

export function getCustomColumns(test) {
    const metrics = getCustomMetrics(test);
    const metricColumns = _.chain(metrics)
        .map((metric) => buildCustomColumn(metric, test))
        .sortBy((column) => !column.isMain)
        .map((column) => _.omit(column, 'isMain'))
        .reduce((buildAry, columnDef) => {
            const { thresholdColumns } = columnDef;
            buildAry.push(_.omit(columnDef, 'thresholdColumns'));

            if (!_.isEmpty(columnDef.thresholdColumns)) {
                buildAry.push(
                    _.get(thresholdColumns, 'degradedColumn', {}),
                    _.get(thresholdColumns, 'excessiveColumn', {})
                );
            }

            return buildAry;
        }, [])
        .value();

    return [
        {
            title: t`Result`,
            id: 'Result-message',
            width: 200,
            sortable: true,
            mapping: 'message',
            renderer: resultColumnRenderer,
        },
        {
            id: 'SEM-message',
            width: 40,
            sortable: false,
            mapping: 'sem-capture-error',
            getTooltip: (row) => semColumnTooltip(row),
            renderer: semColumnRenderer,
        },
        ...metricColumns,
        ...getWiFiGroupTestColumn(test),
        ...getWiFiColumns(test),
    ];
}
